/* eslint-disable @compiled/no-keyframes-tagged-template-expression, @atlaskit/design-system/no-keyframes-tagged-template-expression, @atlaskit/ui-styling-standard/no-unsafe-values, no-unused-vars */
/**
 * @experimental
 * This component is a prototype and is not ready to be used in production.
 */
import React, { useCallback, useState, useEffect, useRef, useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { cssMap, keyframes } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import Button from '@atlaskit/button/default/button';
import MicrophoneIcon from '@atlaskit/icon/core/microphone';
import { token } from '@atlaskit/tokens';
import { fg } from '@confluence/feature-gating';

// Pulse animation for recording indicator
const pulseAnimation = keyframes`
	0%, 100% {
		opacity: 1;
		transform: scale(1);
	}
	50% {
		opacity: 0.6;
		transform: scale(1.15);
	}
`;

const dictationButtonStyles = cssMap({
	recordingDotFallback: {
		position: 'absolute',
		top: '0px',
		right: '-1px',
		width: '4px',
		height: '4px',
		backgroundColor: token('color.background.danger.bold'),
		borderRadius: '50%',
		animation: `${pulseAnimation} 1.2s ease-in-out infinite`,
	},
	recordingDotUpdated: {
		position: 'absolute',
		top: '0px',
		right: '-1px',
		width: '4px',
		height: '4px',
		backgroundColor: token('color.background.danger.bold'),
		borderRadius: token('radius.full'),
		animation: `${pulseAnimation} 1.2s ease-in-out infinite`,
	},
	microphoneContainer: {
		position: 'relative',
		display: 'inline-flex',
		alignItems: 'center',
		marginRight: token('space.050'),
	},
});

interface SpeechRecognition extends EventTarget {
	continuous: boolean;
	interimResults: boolean;
	lang: string;
	start: () => void;
	stop: () => void;
	abort: () => void;
	onresult: (event: SpeechRecognitionEvent) => void;
	onerror: (event: any) => void; // Using any is acceptable for native browser events
	onend: () => void;
}

interface SpeechRecognitionEvent {
	results: SpeechRecognitionResultList;
	resultIndex: number;
}

interface SpeechRecognitionResultList {
	length: number;
	item: (index: number) => SpeechRecognitionResult;
	[index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
	isFinal: boolean;
	length: number;
	item: (index: number) => SpeechRecognitionAlternative;
	[index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionAlternative {
	transcript: string;
	confidence: number;
}

// Declare global variables for browser compatibility
declare global {
	interface Window {
		SpeechRecognition: new () => SpeechRecognition;
		webkitSpeechRecognition: new () => SpeechRecognition;
	}
}

export interface DictationButtonProps {
	userOffline: boolean;
	isDraftLockedForEditing?: boolean;
}

const i18n = defineMessages({
	dictationLabel: {
		id: 'title-area-content-components.toolbar-above-title.dictation-button.label',
		description: 'Accessible label for the dictation/voice typing button icon',
		defaultMessage: 'Dictation',
	},
	voiceTypingText: {
		id: 'title-area-content-components.toolbar-above-title.dictation-button.text',
		description: 'Text displayed on the voice typing button',
		defaultMessage: 'Voice typing',
	},
});

// Helper function to directly test if speech recognition is available
const checkSpeechRecognitionSupport = (): boolean => {
	if (typeof window === 'undefined') return false;

	// First check if the APIs exist
	const hasSpeechRecognition = 'SpeechRecognition' in window;
	const hasWebkitSpeechRecognition = 'webkitSpeechRecognition' in window;

	// Try to create an instance to truly confirm it works
	try {
		if (hasWebkitSpeechRecognition) {
			// Try to create a webkitSpeechRecognition instance
			const WebkitSpeechRecognition = window.webkitSpeechRecognition;
			if (WebkitSpeechRecognition) {
				const recognition = new WebkitSpeechRecognition();
				return !!recognition;
			}
		} else if (hasSpeechRecognition) {
			// Try to create a SpeechRecognition instance
			const SpeechRecognition = window.SpeechRecognition;
			if (SpeechRecognition) {
				const recognition = new SpeechRecognition();
				return !!recognition;
			}
		}
	} catch (error) {
		// Error handling is silent
	}

	return false;
};

export const DictationButton = ({
	userOffline,
	isDraftLockedForEditing,
}: DictationButtonProps): React.JSX.Element => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDictating, setIsDictating] = useState(false);
	const [isSupported, setIsSupported] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const recognitionRef = useRef<SpeechRecognition | null>(null);
	const isMounted = useRef(false);
	const floatingButtonRef = useRef<HTMLDivElement | null>(null);

	// Add ref for tracking session duration
	const sessionStartTimeRef = useRef<number>(0);

	// Track the last inserted transcript to avoid duplicates
	const lastInsertedTranscriptRef = useRef('');

	// Generate unique ID for this component instance to avoid conflicts
	const componentId = useId();
	const componentIdRef = useRef(`dictation-floating-button${componentId}`);

	// Check if browser supports speech recognition after component mounts
	useEffect(() => {
		isMounted.current = true;

		// Use the helper function to check support
		const supported = checkSpeechRecognitionSupport();
		setIsSupported(supported);

		if (!supported) {
			setErrorMessage('Speech recognition not supported in this browser');
		}

		// Create the floating button element when component mounts
		if (typeof document !== 'undefined') {
			// Create floating button with unique ID
			const buttonElement = document.createElement('div');
			buttonElement.setAttribute('id', componentIdRef.current);
			buttonElement.setAttribute('data-testid', 'dictation-floating-button');
			buttonElement.setAttribute('role', 'button');
			buttonElement.setAttribute('aria-label', 'Stop voice typing');
			buttonElement.setAttribute('tabindex', '0');
			document.body.appendChild(buttonElement);
			floatingButtonRef.current = buttonElement;
		}

		return () => {
			isMounted.current = false;

			// Remove the elements when component unmounts
			if (floatingButtonRef.current && document.body.contains(floatingButtonRef.current)) {
				document.body.removeChild(floatingButtonRef.current);
			}
		};
	}, []);

	// Define stopDictation function
	const stopDictation = useCallback(() => {
		if (recognitionRef.current) {
			try {
				recognitionRef.current.stop();
			} catch (error) {
			} finally {
				recognitionRef.current = null;
				setIsDictating(false);

				// Hide the button
				if (floatingButtonRef.current) {
					floatingButtonRef.current.style.display = 'none';
				}

				// Calculate session duration in seconds
				if (sessionStartTimeRef.current > 0) {
					const sessionDurationSeconds = Math.round(
						(Date.now() - sessionStartTimeRef.current) / 1000,
					);

					// Send analytics event with session duration
					createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'completed',
							actionSubject: 'dictationSession',
							actionSubjectId: 'voiceTyping',
							attributes: {
								sessionDurationSeconds,
							},
						},
					}).fire();

					// Reset session start time
					sessionStartTimeRef.current = 0;
				}
			}
		}
	}, [createAnalyticsEvent]);

	// Function to insert final text into the editor
	const insertText = useCallback((text: string) => {
		try {
			const selection = window.getSelection();
			if (!selection || !selection.rangeCount) return;
			const range = selection.getRangeAt(0);

			// Insert final text directly
			range.deleteContents();
			const textNode = document.createTextNode(text);
			range.insertNode(textNode);

			// Move cursor to end of inserted text
			try {
				if (textNode.parentNode) {
					range.selectNode(textNode);
					range.collapse(false); // collapse to end
					selection.removeAllRanges();
					selection.addRange(range);
				}
			} catch (e) {}
		} catch (error) {}
	}, []);

	// Define startDictation function
	const startDictation = useCallback(() => {
		// Double-check we're in a browser environment and API is supported
		if (typeof window === 'undefined') {
			setErrorMessage('Not in a browser environment');
			setIsDictating(false);
			return;
		}

		// Re-check support at runtime
		const supported = checkSpeechRecognitionSupport();
		if (!supported) {
			setErrorMessage('Speech recognition not supported in this browser');
			setIsDictating(false);
			return;
		}

		try {
			// Record start time when dictation begins
			sessionStartTimeRef.current = Date.now();

			// Get the right speech recognition constructor
			const SpeechRecognitionAPI = window.webkitSpeechRecognition || window.SpeechRecognition;

			if (!SpeechRecognitionAPI) {
				setErrorMessage('Speech Recognition API not found');
				setIsDictating(false);
				return;
			}

			// Create a new recognition instance
			const recognition = new SpeechRecognitionAPI();

			// Configure the recognition - disable interim results for simpler handling
			recognition.continuous = true;
			recognition.interimResults = false; // Only show final recognized text

			recognition.lang = 'en-US'; // Default to English

			// Reset transcript tracking when starting new dictation
			lastInsertedTranscriptRef.current = '';

			// Set up the event handlers
			recognition.onresult = (event) => {
				if (!isMounted.current) return;

				// Get the last result (most complete transcript)
				// Since interimResults is false, all results should be final
				const lastResultIndex = event.results.length - 1;
				if (lastResultIndex < 0) return;

				const lastResult = event.results[lastResultIndex];
				if (!lastResult.isFinal) return;

				const newTranscript = lastResult[0].transcript.trim();
				const lastInserted = lastInsertedTranscriptRef.current.trim();

				// Extract only the NEW portion by comparing with what we've already inserted
				if (newTranscript.startsWith(lastInserted)) {
					const newText = newTranscript.slice(lastInserted.length).trim();
					if (newText) {
						// Update the tracker
						lastInsertedTranscriptRef.current = newTranscript;
						// Insert only the new portion
						insertText(`${newText} `);
					}
				} else if (newTranscript !== lastInserted) {
					// Edge case: transcript doesn't start with our last inserted text
					// This could happen if the API resets or there's a mismatch
					// Insert the whole thing and update our tracker
					lastInsertedTranscriptRef.current = newTranscript;
					insertText(`${newTranscript} `);
				}
			};

			// Add network error handling
			recognition.onerror = (event: any) => {
				if (!isMounted.current) return;

				// Handle network errors specifically
				if (event.error === 'network') {
					setErrorMessage('Network error. Please check your connection and try again.');

					// Attempt to restart after a network error
					if (isDictating) {
						setTimeout(() => {
							if (isMounted.current && isDictating) {
								try {
									recognition.start();
								} catch (retryError) {
									setIsDictating(false);
									setErrorMessage('Failed to restart after network error');
								}
							}
						}, 2000); // Give network time to recover
					}
				} else {
					// Original error handling
					const errorMsg = `Recognition error: ${event.error || 'unknown error'}`;
					setErrorMessage(errorMsg);
					setIsDictating(false);

					// If permission denied, show more specific message
					if (event.error === 'not-allowed' || event.error === 'permission-denied') {
						const permissionMsg =
							'Microphone access denied. Please allow microphone access in your browser settings.';
						setErrorMessage(permissionMsg);
					}
				}
			};

			// Add a robust retry mechanism to onend handler
			recognition.onend = () => {
				if (!isMounted.current) return;

				// If we're supposed to be dictating but the recognition stopped, restart it
				if (isDictating) {
					// Track retry attempts
					const maxRetries = 3;
					let retryCount = 0;

					const attemptRestart = () => {
						try {
							// Restart recognition immediately
							recognition.start();
						} catch (error) {
							retryCount++;
							if (retryCount < maxRetries) {
								// If immediate restart fails, try with exponential backoff
								setTimeout(
									() => {
										if (isMounted.current && isDictating) {
											attemptRestart();
										}
									},
									Math.min(100 * Math.pow(2, retryCount), 2000),
								); // Exponential backoff with max 2s
							} else {
								setIsDictating(false);
								setErrorMessage('Connection issue. Please try again.');
							}
						}
					};

					attemptRestart();
				}
			};

			// Start listening
			recognition.start();

			// Show the floating button
			if (floatingButtonRef.current) {
				floatingButtonRef.current.style.display = 'flex';
			}

			// Save the recognition instance to the ref for later access
			recognitionRef.current = recognition;
		} catch (error) {
			if (error instanceof Error) {
				const errorMsg = `Failed to start dictation: ${error.message}`;
				setErrorMessage(errorMsg);
			} else {
				const errorMsg = 'Failed to start dictation: unknown error';
				setErrorMessage(errorMsg);
			}
			setIsDictating(false);
		}
	}, [isDictating, insertText]);

	// Update the button when dictation state changes
	useEffect(() => {
		if (!floatingButtonRef.current) return;

		const button = floatingButtonRef.current;

		if (isDictating) {
			// Find the object sidebar container
			const sidebarContainer = document.querySelector(
				'[data-testid="object-sidebar-container"]',
			) as HTMLElement | null;

			// Set button styles
			Object.assign(button.style, {
				position: 'relative',
				width: '48px',
				height: '48px',
				borderRadius: fg('confluence_frontend_tokenize_border') ? token('radius.full') : '50%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
				zIndex: '1',
				background: token('color.background.danger.bold'),
				color: token('color.text.inverse'),
				cursor: 'pointer',
				marginBottom: token('space.200'),
				marginTop: token('space.negative.100'),
			});

			// Append the button to the sidebar container (renders below Rovo button)
			if (sidebarContainer) {
				button.setAttribute('data-testid', 'dictation-floating-button-sidebar');
				sidebarContainer.appendChild(button);
			} else {
				button.setAttribute('data-testid', 'dictation-floating-button');
				Object.assign(button.style, {
					position: 'fixed',
					bottom: '8px',
					right: '24px',
					marginTop: '0',
				});
			}

			// Set content with microphone icon
			button.innerHTML = `
				<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
					<path d="M12 15C14.21 15 16 13.21 16 11V5C16 2.79 14.21 1 12 1C9.79 1 8 2.79 8 5V11C8 13.21 9.79 15 12 15Z" fill="white"/>
					<path d="M19 11H17.3C17.3 14.07 14.76 16.61 11.69 16.61L11.69 16.61C8.61 16.61 6.07 14.07 6.07 11H4.38C4.38 14.62 7 17.6 10.5 18.24V21H13.5V18.24C17 17.6 19 14.62 19 11Z" fill="white"/>
				</svg>
			`;

			// Add tooltip title
			button.setAttribute('title', 'Click to stop dictation');

			// Add click event listener
			button.onclick = stopDictation;

			// Add keyboard support (Enter and Space keys)
			const handleKeyDown = (e: KeyboardEvent) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					stopDictation();
				}
			};
			button.addEventListener('keydown', handleKeyDown);

			// Cleanup function
			return () => {
				button.removeEventListener('keydown', handleKeyDown);
			};
		} else {
			// Hide the button when not dictating
			button.style.display = 'none';
			button.setAttribute('data-testid', 'dictation-floating-button');
		}
	}, [isDictating, stopDictation]);

	// Stop dictation immediately when user goes offline
	useEffect(() => {
		if ((userOffline || isDraftLockedForEditing) && isDictating) {
			stopDictation();
		}
	}, [userOffline, isDraftLockedForEditing, isDictating, stopDictation]);

	const handleDictationClick = useCallback(() => {
		// Reset any error messages when trying again
		setErrorMessage('');

		// Determine action based on current state
		const willStart = !isDictating;

		// Fire analytics event
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: willStart ? 'started' : 'stopped',
				source: 'dictationButton',
				actionSubject: 'pageTitle',
				actionSubjectId: 'dictationButton',
			},
		}).fire();

		// Perform action immediately without race conditions
		if (willStart) {
			setIsDictating(true);
			// Use setTimeout to allow state update to complete before starting
			setTimeout(() => {
				startDictation();
			}, 0);
		} else {
			stopDictation();
		}
	}, [createAnalyticsEvent, isDictating, startDictation, stopDictation]);

	// Clean up the recognition object when the component unmounts
	useEffect(() => {
		return () => {
			if (recognitionRef.current) {
				try {
					recognitionRef.current.stop();
				} catch (error) {}
			}
		};
	}, []);

	// Get a more specific tooltip message based on state
	const getTooltipMessage = () => {
		if (errorMessage) {
			return errorMessage;
		}
		if (!isSupported) {
			return 'Speech recognition not supported in this browser';
		}
		return isDictating ? 'Stop Voice Typing' : 'Start Voice Typing';
	};

	return (
		<div data-testid="dictation-button-container">
			<Tooltip content={getTooltipMessage()} position="bottom">
				<Button
					appearance="subtle"
					onClick={handleDictationClick}
					isDisabled={userOffline || !!isDraftLockedForEditing}
					testId="dictation-button"
				>
					<span css={dictationButtonStyles.microphoneContainer}>
						<MicrophoneIcon
							label={intl.formatMessage(i18n.dictationLabel)}
							color={isDictating ? token('color.icon.danger') : undefined}
						/>
						{isDictating &&
							(fg('confluence_frontend_tokenize_border') ? (
								<span
									css={dictationButtonStyles.recordingDotUpdated}
									data-testid="recording-indicator"
								/>
							) : (
								<span
									css={dictationButtonStyles.recordingDotFallback}
									data-testid="recording-indicator"
								/>
							))}
					</span>
					{intl.formatMessage(i18n.voiceTypingText)}
				</Button>
			</Tooltip>
		</div>
	);
};
